import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ReviewStatusBadge } from "./StatusBadge"
import { ContextPanel } from "./ContextPanel"
import { EmailBody } from "@/components/portal/EmailBody"
import { ActionPlanCard, projektBezeichnung, projektVerknuepfen, type HeroProjektKurz } from "./ActionPlanCard"
import { usePlanSpeichern } from "@/hooks/usePlanSpeichern"
import { planAbfrage } from "./planAbfrage"
import { DraftPanel } from "./DraftPanel"
import { ClaudeChatBar } from "./ClaudeChatBar"
import { QuickActionChips } from "./QuickActionChips"
import { CreateTaskModal } from "./CreateTaskModal"
import { InterneAufgabeModal } from "@/components/portal/InterneAufgabeModal"
import { NotebookPen } from "lucide-react"
import type { DraftDetail, ActionPlan, ExecutePlanResult, CatalogEntry } from "./types"
import { personName } from "@/lib/utils"
import { betroffenerName, proaktivTitel } from "./proaktiv"
import { ActionConfirmDialog, type ConfirmLine } from "@/components/portal/ActionConfirmDialog"


import { toast } from "@/components/portal/Toast"
import {
  buildBodyWithQuote as _buildBodyWithQuote,
  buildHtmlWithQuote as _buildHtmlWithQuote,
} from "@/lib/replyQuote"
import { PendingCleanupCard } from "@/components/portal/PendingCleanupCard"
import { AutoSaveIndicator } from "@/components/portal/AutoSaveIndicator"
import { HistoryContextBadge } from "@/components/portal/HistoryContextBadge"
import { label, schrittFuerAktion } from "./aktionFelder"
import { readDraftFromStorage, useAutoSaveDraft } from "@/hooks/useAutoSaveDraft"
import { notifyPendingSend, type PendingSendInfo } from "@/lib/undoSend"
import { datumHinweise } from "@/lib/sendePruefung"
import { AusfuehrenAbgelehnt, ausfuehrenUrl, pruefeAusfuehrung } from "@/lib/ausfuehrenAbgelehnt"

/**
 * Review-Card — Herzstueck der Review-Ansicht.
 *
 * Layout:
 *   Header (AI-Avatar + Titel + Status-Badge)
 *   ─── Split-View ──────────────────────────────
 *   [ContextPanel 40%] [DraftPanel 60%]
 *   ─────────────────────────────────────────────
 *   ClaudeChatBar
 *   QuickActionChips
 *   [Senden] [Nur speichern] [Verwerfen]   [Naechster ↓]
 *
 * Keyboard:
 *   Enter   → Senden
 *   E       → Edit-Modus toggle
 *   Cmd+K   → Chat-Bar fokussieren
 *   J       → Naechster Draft
 *   K       → Vorheriger Draft
 *   H       → Ueberspringen (status=reviewed)
 */
export function ReviewCard({
  detail,
  onNext,
  onPrev,
  onDone,
  nurMarkiert = false,
}: {
  detail: DraftDetail
  /** #164: nur wegen der roten Fahne im Review — ohne Entwurf oder schon beantwortet. */
  nurMarkiert?: boolean
  onNext?: () => void
  onPrev?: () => void
  onDone?: () => void  // nach Senden/Verwerfen → Liste neu laden & naechsten auswaehlen
}) {
  // Auto-saved Drafts haben Vorrang vor den AI-/Default-Werten. localStorage-Mirror
  // greift fuer noch nicht zum Server geflushte Edits (z.B. Reload mid-Save).
  // Server-Wert hat Vorrang wenn er neuer ist (cross-tab / cross-device).
  const storageKey = `auto-draft:review:${detail.email_id}`
  const stored = readDraftFromStorage<{ draft: string; recipient: string; subject: string }>(
    storageKey,
    { serverSavedAt: detail.manual_draft_saved_at },
  )
  const [draft, setDraft] = useState(stored?.draft ?? detail.draft_body)
  const [internOffen, setInternOffen] = useState(false)
  const [recipient, setRecipient] = useState(stored?.recipient ?? detail.manual_draft_to ?? detail.recipient)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [mehrOffen, setMehrOffen] = useState(false)
  const [subject, setSubject] = useState(stored?.subject ?? detail.manual_draft_subject ?? detail.reply_subject)
  const [editMode, setEditMode] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  // Katalog-Aktionen ohne eigenen Plan-Schritt lassen sich nicht ueber
  // `step_enabled` abwaehlen — sie brauchen eine eigene Auswahl. Dieselbe
  // Mechanik wie im Posteingang.
  const [abgewaehlteAktionen, setAbgewaehlteAktionen] = useState<Set<string>>(new Set())
  // Der Planer hat keine Antwort vorgesehen, der Nutzer will trotzdem eine
  // schreiben. Bis 06.09.2026 zeigte das Review in diesem Fall den
  // Klassifikator-Entwurf vom Eingang als fertigen Antwortbrief — bei 141 von
  // 252 Mails stand dort ein Text, den der Plan nie senden wollte („Review
  // und E-Mail zeigen andere Plaene").
  const [antwortErzwungen, setAntwortErzwungen] = useState(false)
  // Bestaetigung, bevor etwas nach aussen geht (Paul, 06.09.2026): eine Mail
  // an den Kunden ist das Einzige, was der Kunde sieht — dafuer dieselbe
  // Vorschau wie im Posteingang. Interne Schritte laufen ohne Dialog.
  const [dialog, setDialog] = useState<{ lines: ConfirmLine[]; needsDouble: boolean; weiter: () => void } | null>(null)
  const [vorschauLaeuft, setVorschauLaeuft] = useState(false)
  const chatRef = useRef<HTMLInputElement>(null)
  const qc = useQueryClient()

  // Neuer Detail → States resetten (gleiche Vorrang-Reihenfolge wie beim Mount).
  useEffect(() => {
    const s = readDraftFromStorage<{ draft: string; recipient: string; subject: string }>(
      storageKey,
      { serverSavedAt: detail.manual_draft_saved_at },
    )
    setDraft(s?.draft ?? detail.draft_body)
    setRecipient(s?.recipient ?? detail.manual_draft_to ?? detail.recipient)
    setSubject(s?.subject ?? detail.manual_draft_subject ?? detail.reply_subject)
    setEditMode(false)
    setAttachments([])
    setAntwortErzwungen(false)
    setDialog(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail.email_id])

  // Auto-Save: body geht in draft_response, subject + recipient in manual_draft_*.
  const autoSaveValues = useMemo(
    () => ({ draft, recipient, subject }),
    [draft, recipient, subject],
  )
  const autoSave = useCallback(
    async (v: { draft: string; recipient: string; subject: string }) => {
      const r = await fetch(`/api/v1/drafts/${detail.email_id}/save`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: v.draft, subject: v.subject, recipient: v.recipient }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    [detail.email_id],
  )
  const { status: saveStatus, savedAt } = useAutoSaveDraft({
    values: autoSaveValues,
    save: autoSave,
    storageKey,
  })

  // Toggle "Verlauf mitsenden" (default an). Wenn aus → quotes weglassen.
  const [includeQuote, setIncludeQuote] = useState(true)

  // Zitat-Erzeugung liegt in lib/replyQuote — dieselbe Funktion nutzt der
  // Posteingang. Vorher waren das zwei lokale Helfer hier, und dieselbe
  // Antwort sah beim Kunden je nach Absende-Ort anders aus.
  const buildBodyWithQuote = (currentDraft: string): string =>
    _buildBodyWithQuote(currentDraft, detail, includeQuote)
  const buildHtmlWithQuote = (currentDraft: string): string =>
    _buildHtmlWithQuote(currentDraft, detail, includeQuote)

  // Agent-Plan laden (cached, Claude-Call beim ersten Aufruf).
  // User-Edits werden direkt in den Query-Cache geschrieben (siehe setEditedPlan),
  // damit sie ueber Component-Unmounts (Reiter-Wechsel) hinweg ueberleben.
  // Dieselbe Abfrage stoesst die Review-Seite schon parallel zum Detail an (#158).
  const planQuery = useQuery<ActionPlan>(planAbfrage(detail.email_id))
  const plan = planQuery.data
  // 🔴 Der Cache allein reicht nicht (#40): der Plan-Endpunkt rechnet bei jedem
  // Laden neu, und `staleTime` ist fuenf Minuten. Ein Refetch — Fensterfokus
  // genuegt — stellte den Serverstand wieder her und warf die Entscheidung des
  // Nutzers still weg.
  const planSpeichern = usePlanSpeichern(detail.email_id)
  // Sieht der Plan eine Antwort vor? Wenn nicht, ist `detail.draft_body` der
  // Klassifikator-Entwurf vom Eingang (claude_email.py) — der Planer
  // ueberschreibt `draft_response` nur, wenn er selbst einen Text hat.
  const planSiehtAntwortVor = !!plan && (plan.reply?.body || "").trim() !== ""
  // Der Entwurf ist zu sehen, wenn der Plan ihn vorsieht, es keinen Plan gibt
  // (klassischer Weg) oder der Nutzer ihn ausdruecklich haben will.
  const antwortMoeglich = !plan || planSiehtAntwortVor || antwortErzwungen
  const setEditedPlan = (roh: ActionPlan | null) => {
    if (!roh) return
    let next = roh
    // Wer im Plan die Antwort-Zeile anhakt oder „Antwort senden" aus dem
    // Katalog nachtraegt, will antworten — auch ohne den Knopf „Trotzdem
    // antworten".
    const hatteAntwortAktion = (plan?.actions || []).some((a) => a.type === "antwort_senden")
    const hatAntwortAktion = (next.actions || []).some((a) => a.type === "antwort_senden")
    if (!planSiehtAntwortVor && (next.step_enabled?.reply || (hatAntwortAktion && !hatteAntwortAktion))) {
      setAntwortErzwungen(true)
      next = { ...next, step_enabled: { ...(next.step_enabled || ALLE_SCHRITTE), reply: true } }
    }
    qc.setQueryData(["draft-plan", detail.email_id], next)
    planSpeichern(next as unknown as Record<string, unknown>)
  }
  // Sobald es einen Plan gibt, zeigt das Review die Plan-Karte — dieselbe
  // Sicht wie der Posteingang. Bis 06.09.2026 kam sie nur bei Termin- und
  // Aufgaben-Szenarien; bei `reply_only` und `skip` sah man nur Kontext und
  // Entwurf, und die Katalog-Aktionen des Plans (Dokument ablegen, Angebot,
  // Wiedervorlage) waren von hier aus weder sichtbar noch ausfuehrbar.
  // Newsletter gehen vorher in die NewsletterCard. Ohne Plan (Fehler beim
  // Laden) bleibt der klassische Weg: Kontext + Entwurf + Senden.
  const isAgentScenario = !!plan
  // Wird beim Bestaetigen eine Mail rausgehen?
  const antwortAktiv = antwortMoeglich && (!plan || plan.step_enabled?.reply !== false)
  // Die Karte zeigt die Antwort-Zeile abgehakt, solange der Plan keine
  // Antwort vorsieht und niemand sie erzwungen hat. Haken setzen = erzwingen.
  const planFuerKarte: ActionPlan | undefined = plan && !antwortMoeglich
    ? { ...plan, step_enabled: { ...(plan.step_enabled || ALLE_SCHRITTE), reply: false } }
    : plan
  const antwortTrotzdem = () => {
    if (!plan) return
    setAntwortErzwungen(true)
    setEditedPlan({ ...plan, step_enabled: { ...(plan.step_enabled || ALLE_SCHRITTE), reply: true } })
    setEditMode(true)
  }

  // Der Aktions-Katalog — was der Planer NICHT vorgesehen hat, laesst sich
  // nachtragen. Dieselbe Liste wie im Posteingang.
  const katalog = useQuery<{ items: CatalogEntry[] }>({
    queryKey: ["actions-catalog"],
    queryFn: async () => {
      const r = await fetch("/api/v1/drafts/actions/catalog", { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return await r.json() as { items: CatalogEntry[] }
    },
    staleTime: 60 * 60 * 1000,
    enabled: !!plan,
  })
  const nachtragbar = useMemo(() => {
    const geplant = new Set((plan?.actions || []).map((a) => a.type))
    const offen = (katalog.data?.items ?? [])
      .filter((k) => k.implemented && !geplant.has(k.key))
      .sort((a, b) => a.order - b.order)
    const FEST = ["kontakt_anlegen", "projekt_anlegen", "termin_buchen", "aufgabe_anlegen"]
    return {
      fest: offen.filter((k) => FEST.includes(k.key)),
      weitere: offen.filter((k) => !FEST.includes(k.key)),
    }
  }, [katalog.data, plan?.actions])

  // Was HERO zum Kontakt schon hat — fuer die Warnung vor dem Doppel-Projekt.
  // Der Executor nimmt ein bestehendes Projekt nur ueber `plan.project.id`
  // oder `email_log.hero_project_id`; sind beide leer, entsteht ein zweites.
  const heroProjekte = useQuery<{ projects?: HeroProjektKurz[] }>({
    queryKey: ["email-hero-projects", detail.email_id],
    queryFn: async () => {
      const r = await fetch(`/api/v1/emails/${detail.email_id}/hero-projects`, { credentials: "include" })
      if (!r.ok) return {}
      return await r.json()
    },
    staleTime: 1000 * 30,
    enabled: !!plan,
  })
  const legtProjektAn = !!plan && plan.step_enabled?.project !== false && !plan.project?.id && (
    plan.scenario === "neukunde_termin" || plan.scenario === "bestandskunde_termin"
    || (plan.actions || []).some((a) => a.type === "projekt_anlegen" && !a.done)
  )
  const vorhandeneProjekte = heroProjekte.data?.projects || []
  const projektWaereDoppelt = legtProjektAn && vorhandeneProjekte.length > 0

  /** Was an den Verteiler geht — fuer Vorschau und Ausfuehrung dasselbe. */
  const bauePayload = (args?: { proposalCount?: number; forceBook?: boolean; trotzDoppelt?: boolean }) => {
      const proposalCount = args?.proposalCount
      const forceBook = args?.forceBook === true
      // WICHTIG: Plan direkt aus dem Query-Cache lesen — sonst kann ein
      // gerade getaetigter UI-Edit (Monteur-Pille-Klick → setQueryData) durch
      // stale Closure verloren gehen.
      const currentPlan = qc.getQueryData<ActionPlan>(["draft-plan", detail.email_id]) || plan
      if (!currentPlan) return null
      // proposalCount: 1|2|3 — falls 1, slot_secondary+tertiary leer machen.
      // Falls 2, nur slot_tertiary leer. Default (undefined): alle wie im Plan.
      const empty_slot = { start_datetime: "", duration_minutes: 45, partner_id: null }
      const slotS = proposalCount && proposalCount < 2 ? empty_slot : (currentPlan.slot_secondary || empty_slot)
      const slotT = proposalCount && proposalCount < 3 ? empty_slot : (currentPlan.slot_tertiary || empty_slot)
      // Wenn Attachments hochgeladen sind: Reply via Plan deaktivieren, danach
      // separat per /reply mit Attachments senden (Plan-/execute kennt keine
      // Attachments; FormData waere ein API-Bruch).
      const hasAttachments = attachments.length > 0
      // Die Antwort geht nur raus, wenn sie zu sehen war und angehakt ist.
      // Sieht der Plan keine vor, steht in `draft` der Klassifikator-Entwurf —
      // der darf nicht stillschweigend mitgehen.
      const antwortSenden = antwortAktiv && !hasAttachments && !!draft.trim()
      const stepEnabled = {
        ...(currentPlan.step_enabled || ALLE_SCHRITTE),
        reply: antwortSenden,
      }
      // Body inkl. Original-Mail-Quote (Empfaenger sieht den Verlauf).
      // Plain-Text als Fallback + HTML-Body fuer multipart/alternative
      // (sonst kollabiert der Verlauf beim Empfaenger zu einer Plain-Text-Wand).
      const replyBodyWithQuote = buildBodyWithQuote(draft)
      const replyHtmlWithQuote = buildHtmlWithQuote(draft)
      const merged: ActionPlan = {
        ...currentPlan,
        slot_secondary: slotS,
        slot_tertiary: slotT,
        reply: { subject, body: replyBodyWithQuote, body_html: replyHtmlWithQuote, to: recipient },
        step_enabled: stepEnabled,
      }
      // 🔴 Bis 07.08.2026 rief das Review `/execute` — also `execute_plan` mit
      // den fuenf fest verdrahteten Schritten. Die Katalog-Aktionen (Angebot,
      // Wiedervorlage, Weiterleitung, …) liefen von hier aus NIE, und die
      // Karte zeigte sie deshalb auch nicht an. Wer eine Mail im Review
      // bestaetigte, bekam stillschweigend weniger als im Posteingang.
      //
      // Jetzt derselbe Verteiler wie dort: ein Ausfuehrungspfad, ein
      // Verhalten. `execute_actions` reicht die Alt-Schritte weiterhin an
      // `execute_plan` durch — der erprobte Code bleibt der erprobte Code.
      const alleAktionen = [...(merged.actions || [])]
      // „Trotzdem antworten": der Plan hatte keine Antwort, also auch keine
      // Aktion dafuer. Der Verteiler fuehrt nur aus, was in der Liste steht.
      if (antwortSenden && !alleAktionen.some((a) => a.type === "antwort_senden")) {
        alleAktionen.push({ type: "antwort_senden", params: {} })
      }
      const aktiv = alleAktionen.filter((a) => {
        const schritt = schrittFuerAktion(a.type)
        // Aktion mit Alt-Schritt: der Haken am Schritt entscheidet.
        if (schritt) return (merged.step_enabled ?? {})[schritt] !== false
        // Aktion ohne Alt-Schritt: die eigene Abwahl entscheidet.
        return !abgewaehlteAktionen.has(a.type)
      })
      const url = ausfuehrenUrl(detail.email_id, { forceBook, trotzDoppelt: args?.trotzDoppelt })
      return { url, merged, aktiv, antwortSenden, hasAttachments, replyBodyWithQuote }
  }

  const executePlan = useMutation({
    mutationFn: async (args?: { proposalCount?: number; forceBook?: boolean; trotzDoppelt?: boolean }) => {
      const payload = bauePayload(args)
      if (!payload) throw new Error("Kein Plan geladen")
      const { url, merged, aktiv, hasAttachments, replyBodyWithQuote } = payload
      const r = await fetch(url, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: merged, actions: aktiv }),
      })
      await pruefeAusfuehrung(r)
      // Wenn Plan ok UND Attachments vorhanden → separat Reply mit Attachments
      if (r.ok && hasAttachments) {
        const fd = new FormData()
        fd.append("to", recipient)
        fd.append("subject", subject)
        fd.append("body", replyBodyWithQuote)
        for (const f of attachments) {
          fd.append("attachments", f, f.name)
        }
        const r2 = await fetch(`/api/v1/emails/${detail.email_id}/reply`, {
          method: "POST", credentials: "include", body: fd,
        })
        if (!r2.ok) {
          const txt = await r2.text().catch(() => "")
          throw new Error(`Plan ok aber Reply-mit-Anhaengen fehlgeschlagen: HTTP ${r2.status}: ${txt.slice(0, 200)}`)
        }
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return await r.json() as ExecutePlanResult
    },
    onSuccess: (result) => {
      if (result.ok) {
        const steps = result.completed.join(", ")
        // Phase 6.4 Soft-Send: agent_executor schickt pending_send_id im
        // result.results mit, wenn der Reply queued wurde (30s-Stoppen-Window).
        try {
          const r = (result.results || {}) as Record<string, unknown>
          if (r.pending_send_id && r.scheduled_at) {
            notifyPendingSend({
              pending_send_id: Number(r.pending_send_id),
              scheduled_at: String(r.scheduled_at),
              delay_s: typeof r.delay_s === "number" ? r.delay_s : 30,
            } satisfies PendingSendInfo, `Antwort an ${String(r.sent_to ?? "")}`)
          }
        } catch { /* ignore */ }
        // Auto-Cleanup-Detail: wenn beim Senden BLOCKER mit-geloescht wurden,
        // dem User die konkreten Termine zeigen (Datum + Titel).
        const cleanupDetails = (result.results as Record<string, unknown> | undefined)?.auto_cleanup_deleted_details as
          | Array<{ event_id: number; title: string; start_time: string }>
          | undefined
        if (cleanupDetails && cleanupDetails.length > 0) {
          const list = cleanupDetails.map((d) => {
            const when = (() => {
              try {
                return new Date(d.start_time).toLocaleString("de-DE", {
                  weekday: "short", day: "2-digit", month: "2-digit",
                  hour: "2-digit", minute: "2-digit",
                })
              } catch { return d.start_time || "?" }
            })()
            const title = d.title ? d.title.replace(/^\[BLOCKER\]\s*/i, "") : "(ohne Titel)"
            return `${when} (${title})`
          }).join(", ")
          toast(`✓ Plan ausgefuehrt: ${steps} · Geloescht: ${list}`)
        } else {
          toast(`✓ Agent-Plan ausgefuehrt: ${steps}`)
        }
        try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
        qc.invalidateQueries({ queryKey: ["drafts"] })
        onDone?.()
      } else if (result.failed?.error === "slots_stale" || result.failed?.error === "slot_live_busy") {
        // slots_stale: Partner-Picker konnte keinen freien Monteur ermitteln
        //              (Cache-basiert, ggf. veraltet).
        // slot_live_busy: HERO-Live-Check direkt vor dem CREATE hat eine
        //                 konkrete Doppelbuchung erkannt (frischer als Cache).
        // In beiden Faellen darf der User explizit ueberbuchen — Confirm-Popup.
        const cp = qc.getQueryData<ActionPlan>(["draft-plan", detail.email_id]) || plan
        const sdt = cp?.slot?.start_datetime || ""
        const dur = cp?.slot?.duration_minutes
        const slotInfo = sdt
          ? `Slot: ${sdt.replace("T", " ")}${dur ? ` (${dur} Min)` : ""}`
          : "Slot unbekannt"
        const isLiveBusy = result.failed?.error === "slot_live_busy"
        const headline = isLiveBusy
          ? "Achtung: Doppelbuchung."
          : "Slot ist bereits belegt."
        const detailLine = isLiveBusy && result.failed?.message
          ? `\n\n${String(result.failed.message)}`
          : ""
        const ok = window.confirm(
          `${headline}\n\n${slotInfo}${detailLine}\n\n` +
          "Trotzdem buchen? Der Termin wird mit dem Haupt-Monteur " +
          "ausgefuehrt, auch wenn er zu der Zeit einen anderen Termin hat.\n\n" +
          "OK = Trotzdem buchen   ·   Abbrechen = Plan neu laden mit anderem Slot"
        )
        if (ok) {
          executePlan.mutate({ forceBook: true })
        } else {
          toast("Plan wird neu geladen — anderer Slot wird gesucht", "error")
          qc.invalidateQueries({ queryKey: ["draft-plan", detail.email_id] })
        }
      } else {
        toast(`Fehler bei Schritt ${result.failed?.step}: ${result.failed?.error}`, "error")
      }
    },
    onError: (e, args) => {
      // #194: nichts ausgefuehrt — neuer Plan bzw. bewusste zweite Antwort.
      if (e instanceof AusfuehrenAbgelehnt && e.code === "plan_veraltet") {
        toast(e.message, "warn")
        qc.invalidateQueries({ queryKey: ["draft-plan", detail.email_id] })
        return
      }
      if (e instanceof AusfuehrenAbgelehnt && e.code === "doppelte_antwort") {
        setDialog({
          lines: [
            { type: "antwort_senden", text: e.message, risk: "external", implemented: true },
            { type: "hinweis", text: "Nichts wurde ausgefuehrt. „Bestätigen“ sendet die zweite Antwort und fuehrt den Plan aus.", risk: "internal" },
          ],
          needsDouble: true,
          weiter: () => executePlan.mutate({ ...(args ?? {}), trotzDoppelt: true }),
        })
        return
      }
      toast("Plan-Execute fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    },
  })

  /** Ausfuehren — mit Vorschau-Dialog, sobald eine Mail rausgeht. */
  const starteAusfuehrung = async (args?: { proposalCount?: number; forceBook?: boolean }) => {
    const payload = bauePayload(args)
    if (!payload) return
    if (!payload.antwortSenden && !payload.hasAttachments) {
      executePlan.mutate(args)
      return
    }
    const ersatz: ConfirmLine[] = [
      { type: "antwort_senden", text: `Antwort an ${recipient} senden`, risk: "external", implemented: true },
    ]
    setVorschauLaeuft(true)
    try {
      const r = await fetch(`/api/v1/drafts/${detail.email_id}/preview-actions`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: payload.merged, actions: payload.aktiv }),
      })
      const v = r.ok ? await r.json() as { lines?: ConfirmLine[]; needs_double_confirmation?: boolean } : null
      const lines = (v?.lines && v.lines.length > 0) ? v.lines : ersatz
      setDialog({ lines, needsDouble: v?.needs_double_confirmation ?? true, weiter: () => executePlan.mutate(args) })
    } catch {
      setDialog({ lines: ersatz, needsDouble: true, weiter: () => executePlan.mutate(args) })
    } finally {
      setVorschauLaeuft(false)
    }
  }

  const regenerate = useMutation({
    mutationFn: async (prompt: string) => {
      const r = await fetch(`/api/v1/emails/${detail.email_id}/generate-draft`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, current_draft: draft }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const j = await r.json() as { draft: string }
      return j.draft
    },
    onSuccess: (newDraft) => {
      setDraft(newDraft)
      toast("Draft aktualisiert")
    },
    onError: (e) => toast("Regenerieren fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  // Re-Plan (Agent-2): bei isAgentScenario uebernimmt diese Mutation die ChatBar.
  // User: "lieber Donnerstag 14h" → Claude passt den Plan an, draft & subject werden neu gesetzt.
  const replan = useMutation({
    // String = Hinweis aus der Chatleiste. Objekt = Eskalation auf das
    // staerkere Modell, mit oder ohne Hinweis.
    mutationFn: async (arg: string | { hint: string; stark?: boolean }) => {
      const { hint, stark } = typeof arg === "string" ? { hint: arg, stark: false } : arg
      const r = await fetch(`/api/v1/drafts/${detail.email_id}/replan`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hint, current_plan: plan ?? null, stark: !!stark }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return await r.json() as ActionPlan
    },
    onSuccess: (newPlan) => {
      // User-Picks (partner_id pro Slot, step_enabled) aus altem Plan bewahren —
      // sonst werden Klicks auf Monteur-Pillen vom Re-Plan ueberschrieben (Race).
      const old = qc.getQueryData<ActionPlan>(["draft-plan", detail.email_id])
      const mergeSlot = <T extends { partner_id?: number | null }>(
        next: T, prev: T | undefined,
      ): T => (prev?.partner_id != null && next?.partner_id == null
        ? { ...next, partner_id: prev.partner_id }
        : next)
      const merged: ActionPlan = {
        ...newPlan,
        slot: mergeSlot(newPlan.slot, old?.slot),
        slot_secondary: mergeSlot(newPlan.slot_secondary, old?.slot_secondary),
        slot_tertiary: mergeSlot(newPlan.slot_tertiary, old?.slot_tertiary),
        step_enabled: old?.step_enabled ?? newPlan.step_enabled,
      }
      // Plan direkt in den Query-Cache schreiben — KEIN invalidate, sonst
      // wuerde der gerade angepasste Plan vom Server-Cache ueberschrieben.
      setEditedPlan(merged)
      if (newPlan.reply.body) setDraft(newPlan.reply.body)
      if (newPlan.reply.subject) setSubject(newPlan.reply.subject)
      toast("Plan angepasst")
    },
    onError: (e) => toast("Re-Plan fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  const send = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append("to", recipient)
      fd.append("subject", subject)
      // Body inkl. Original-Mail-Quote anhaengen (Mail-Verlauf fuer Empfaenger)
      fd.append("body", buildBodyWithQuote(draft))
      // User-hochgeladene Anhaenge anhaengen
      for (const f of attachments) {
        fd.append("attachments", f, f.name)
      }
      const r = await fetch(`/api/v1/emails/${detail.email_id}/reply`, {
        method: "POST", credentials: "include", body: fd,
      })
      if (!r.ok) {
        const txt = await r.text().catch(() => "")
        throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`)
      }
    },
    onSuccess: () => {
      toast("✓ Gesendet")
      try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
      qc.invalidateQueries({ queryKey: ["drafts"] })
      onDone?.()
    },
    onError: (e) => toast("Senden fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  const discard = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/v1/drafts/${detail.email_id}/discard`, {
        method: "POST", credentials: "include",
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: () => {
      toast("✓ Erledigt")
      try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
      qc.invalidateQueries({ queryKey: ["drafts"] })
      onDone?.()
    },
    onError: (e) => toast("Aktion fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  // Agent-4: 1-Klick-Weiterleitung wenn forward_suggestion gematcht hat
  const forwardEmail = useMutation({
    mutationFn: async (forward_to: string) => {
      const r = await fetch(`/api/v1/drafts/${detail.email_id}/forward`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ forward_to }),
      })
      if (!r.ok) {
        const txt = await r.text().catch(() => "")
        throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`)
      }
      return { data: await r.json(), forward_to }
    },
    onSuccess: (res) => {
      // Phase 6.4 Soft-Send Toast (forward queued)
      try {
        notifyPendingSend(
          res.data as PendingSendInfo,
          `Weiterleitung an ${res.forward_to}`,
        )
      } catch { /* ignore */ }
      qc.invalidateQueries({ queryKey: ["drafts"] })
      onDone?.()
    },
    onError: (e) => toast("Weiterleiten fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  // Save-only: speichert Draft-Text + Empfaenger + Betreff 1:1 (kein Claude-Call, kein Senden).
  // Auto-Save erledigt das ohnehin debounced — der Button ist explizit fuer "jetzt sofort"
  // und nicht zu umgehen falls der Auto-Save mal in einem Error-State haengt.
  const saveOnly = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/v1/drafts/${detail.email_id}/save`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ body: draft, subject, recipient }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    },
    onSuccess: () => {
      toast("Draft gespeichert")
      try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
      qc.invalidateQueries({ queryKey: ["drafts"] })
    },
    onError: (e) => toast("Speichern fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
  })

  // Was der Bestaetigen-Knopf ausfuehren wird — Alt-Schritte plus die
  // Katalog-Aktionen ohne eigenen Schritt. Steht nichts drin, ist
  // „Ohne Antwort erledigt" die Primaeraktion.
  const weitereGewaehlt = (plan?.actions || [])
    .filter((a) => schrittFuerAktion(a.type) === null && !a.done && !abgewaehlteAktionen.has(a.type))
    .map((a) => label(a.type))
  const knopfText = agentButtonLabel(planFuerKarte, antwortAktiv && !!draft.trim(), weitereGewaehlt)
  const nichtsGewaehlt = knopfText === "✓ Nichts ausgewaehlt"

  const starteSenden = async () => {
    // #92: Datum-/Zeitfenster-Hinweise an der Zeile — nur Hinweis, keine Sperre.
    const hinweise = await datumHinweise(detail.email_id, subject, draft)
    setDialog({
      lines: [{ type: "antwort_senden", text: `Antwort an ${recipient} senden`, risk: "external", implemented: true,
                warnung: hinweise.length ? hinweise.join(" ") : null }],
      needsDouble: true,
      weiter: () => send.mutate(),
    })
  }
  const starteWeiterleiten = (to: string) => setDialog({
    lines: [{ type: "weiterleiten", text: `Mail an ${to} weiterleiten`, risk: "external", implemented: true }],
    needsDouble: false,
    weiter: () => forwardEmail.mutate(to),
  })

  // Keyboard-Shortcuts
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const target = ev.target as HTMLElement
      const inInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA"

      // Cmd/Ctrl+K = Chat-Bar fokussieren (auch aus Inputs)
      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === "k") {
        ev.preventDefault()
        chatRef.current?.focus()
        return
      }

      // Esc = Edit-Mode verlassen
      if (ev.key === "Escape" && editMode) {
        ev.preventDefault()
        setEditMode(false)
        return
      }

      // Die restlichen Shortcuts nur wenn Fokus NICHT in Input/Textarea
      if (inInput) return

      if (ev.key === "Enter") {
        ev.preventDefault()
        if (dialog) return
        if (isAgentScenario && plan) {
          if (!executePlan.isPending && !vorschauLaeuft && !nichtsGewaehlt) void starteAusfuehrung(undefined)
        } else {
          if (!send.isPending) void starteSenden()
        }
      } else if (ev.key.toLowerCase() === "e") {
        ev.preventDefault()
        setEditMode((v) => !v)
      } else if (ev.key.toLowerCase() === "j") {
        ev.preventDefault()
        onNext?.()
      } else if (ev.key.toLowerCase() === "k") {
        ev.preventDefault()
        onPrev?.()
      } else if (ev.key.toLowerCase() === "h") {
        ev.preventDefault()
        onDone?.()
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, send, executePlan, isAgentScenario, plan, onNext, onPrev, onDone, dialog, vorschauLaeuft])

  const received = detail.received_at ? new Date(detail.received_at) : null
  const receivedAgo = received ? timeAgo(received) : ""
  const isNewsletter = detail.handling_class === "newsletter"

  // Newsletter: simpleres UI — nur Original-Mail + Archivieren-Button
  if (isNewsletter) {
    return (
      <NewsletterCard
        detail={detail}
        onArchive={() => discard.mutate()}
        archiving={discard.isPending}
        onNext={onNext}
        onForward={(to) => forwardEmail.mutate(to)}
        forwarding={forwardEmail.isPending}
      />
    )
  }

  return (
    <div className="review-karte" style={{
      background: "#fff", borderRadius: 12, border: "0.5px solid rgba(44,44,42,0.15)",
      padding: 20, display: "flex", flexDirection: "column", gap: 14, height: "100%",
    }}>
      {/* Header */}
      {/* Kopf darf umbrechen: Bei 1024 px drueckten die drei Abzeichen rechts
          den Namen so weit zusammen, dass sie sich optisch ueberlagerten. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{
          width: 22, height: 22, borderRadius: "50%", background: "#3C3489", color: "#fff",
          fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>AI</div>
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          {/* Nur der Name — die Adresse stand hier in voller Laenge und
              machte aus der Ueberschrift eine abgeschnittene Zeile. */}
          <div
            data-pii
            style={{ fontSize: 15, fontWeight: 500, color: "#2C2C2A" }}
            title={detail.sender || undefined}
          >
            {detail.is_proactive
              ? `${proaktivTitel(detail.proactive_trigger)} – ${betroffenerName(detail.sender, detail.sender_email)}`
              : `${antwortMoeglich ? "Antwort vorbereitet" : "Plan vorbereitet"} – ${personName(detail.sender)}`}
          </div>
          <div style={{ fontSize: 12, color: "#888780", marginTop: 2 }}>
            <span data-pii>{detail.subject || "(ohne Betreff)"}</span>
            {receivedAgo && <> · vor {receivedAgo}</>}
            {" · "}
            <span style={{ color: "#5F5E5A" }}>{detail.tenant_name}</span>
          </div>
          {/* Das Review zeigt EINE Mail, der Posteingang je Verlauf die
              neueste. Ohne diese Zeile vergleicht man zwei verschiedene Mails
              und meldet „andere Plaene" (44 von 252 Review-Mails, 06.09.2026). */}
          <ThreadStandZeile detail={detail} />
        </div>
        <HistoryContextBadge emailId={detail.email_id} />
        {/* #13: interne Aufgabe direkt aus dem Review — derselbe Dialog wie
            in der Mail-Leiste, mit Rueckweg zur Mail. Nur auf Klick (B9). */}
        <button
          type="button"
          className="pp-btn pp-btn-sm"
          onClick={() => setInternOffen(true)}
          title="Interne Aufgabe zu dieser Mail anlegen — bleibt im Portal"
          style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12 }}
        >
          <NotebookPen className="w-3 h-3" /> Interne Aufgabe
        </button>
        <InterneAufgabeModal
          open={internOffen}
          onClose={() => setInternOffen(false)}
          tenantSlug={detail.tenant_slug || null}
          emailId={detail.email_id}
          heroProjectId={plan?.project?.id ?? null}
          titelVorschlag={(detail.subject || "").slice(0, 120) || null}
        />
        <AutoSaveIndicator status={saveStatus} savedAt={savedAt} />
        <ReviewStatusBadge confidence={detail.confidence} />
      </div>

      {/* #164: Markierte Mails bleiben im Review, auch wenn sie erledigt oder
          beantwortet sind. Dann darf der Plan nicht aussehen wie offen — sonst
          geht eine zweite Antwort an den Kunden. */}
      {nurMarkiert && (
        <div data-testid="hinweis-nur-markiert" style={{
          fontSize: 12, color: "#791F1F", background: "#FCEBEB",
          border: "0.5px solid #F09595", borderRadius: 6, padding: "6px 10px",
        }}>
          Diese Mail steht hier, weil Sie sie markiert haben — es gibt keinen
          offenen Entwurf, oder es ging schon eine Antwort raus. Bitte vor dem
          Senden prüfen, dass nichts doppelt rausgeht. Zum Herausnehmen die
          Fahne entfernen oder die Mail erledigen.
        </div>
      )}

      {/* Cleanup-Sektion: zwei HERO-Termine wurden vorgeschlagen, Kunde hat geantwortet */}
      {detail.pending_event_cleanup && (
        <PendingCleanupCard
          cleanup={detail.pending_event_cleanup}
          autoConfirmedSlotIso={
            plan?.customer_confirmed_slot && plan?.slot?.start_datetime
              ? plan.slot.start_datetime
              : null
          }
          onDone={() => {
            qc.invalidateQueries({ queryKey: ["draft-detail", detail.email_id] })
          }}
        />
      )}

      {projektWaereDoppelt && (
        <div style={{
          fontSize: 12, color: "#9a3412", background: "#FFF7ED",
          border: "0.5px solid rgba(154,52,18,0.3)", borderRadius: 6, padding: "6px 10px",
        }}>
          Der Kontakt hat bereits {vorhandeneProjekte.length === 1 ? "ein Projekt" : `${vorhandeneProjekte.length} Projekte`}
          {vorhandeneProjekte[0]?.code ? ` (${vorhandeneProjekte[0].code})` : ""} — „Projekt anlegen" erzeugt ein weiteres.
          {/* #140: direkt hier verknuepfen — der Verweis auf „Bearbeiten"
              fuehrte ins Leere. Schreibt nur den Plan, nicht HERO. */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            {vorhandeneProjekte.slice(0, 4).map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid="projekt-stattdessen-verknuepfen"
                onClick={() => plan && setEditedPlan({ ...plan, project: projektVerknuepfen(plan.project, p) })}
                style={{
                  fontSize: 12, padding: "3px 8px", borderRadius: 6, cursor: "pointer",
                  border: "0.5px solid rgba(154,52,18,0.45)", background: "#fff", color: "#9a3412",
                }}
              >
                Stattdessen verknüpfen: {projektBezeichnung(p)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Split-View */}
      {/* Zwei Spalten nur solange beide lesbar bleiben — siehe .review-split
          in admin-theme.css. Bei 1024 px blieben dem Entwurf 271 px, und
          „An" und „Betreff" waren nur noch angeschnitten zu sehen. */}
      <div className="review-split" style={{ flex: 1, minHeight: 0 }}>
        {isAgentScenario && planFuerKarte ? (
          <ActionPlanCard
            plan={planFuerKarte}
            detail={detail}
            editable
            nachtragbar={nachtragbar}
            onPlanChange={(next) => setEditedPlan(next)}
            onBodyRefreshed={(newBody) => setDraft(newBody)}
            aktionen={(planFuerKarte.actions || []).map((a) => ({
              type: a.type,
              label: label(a.type),
              // `done` setzt der Plan-Endpunkt aus der Mail-Zeile und einem
              // Vermerk im gespeicherten Plan — dieselbe Quelle wie im
              // Posteingang. `completed` waere die falsche: die spricht
              // Executor-Sprache ("create_contact"), nicht Katalog-Sprache.
              fertig: a.done === true,
              an: !abgewaehlteAktionen.has(a.type),
            }))}
            onToggleAktion={(type) => setAbgewaehlteAktionen((s) => {
              const next = new Set(s)
              next.has(type) ? next.delete(type) : next.add(type)
              return next
            })}
          />
        ) : <ContextPanel detail={detail} />}
        {/* Phase 7+ (11.05.): Kontaktformular-Resolver — Banner wenn die
            Reply-Adresse nicht der Mail-Sender ist sondern aus dem Body extrahiert */}
        {detail.effective_reply_to && (
          <div style={{
            marginTop: 8, padding: "8px 12px",
            background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 6,
            fontSize: 12, color: "#1e40af",
          }}>
            💡 <strong>Kontaktformular erkannt</strong> — Antwort geht an{" "}
            <strong data-pii>{detail.effective_reply_to}</strong> (aus Mail-Body extrahiert,
            nicht an den technischen Sender <code data-pii>{detail.sender_email}</code>)
          </div>
        )}
        {antwortMoeglich ? (
          <DraftPanel
            detail={detail}
            draft={draft}
            onDraftChange={setDraft}
            recipient={recipient}
            onRecipientChange={setRecipient}
            subject={subject}
            onSubjectChange={setSubject}
            editMode={editMode}
            onToggleEdit={() => setEditMode((v) => !v)}
            hinweis={plan && !planSiehtAntwortVor ? (
              <>Dieser Text stammt <strong>nicht aus dem Plan</strong>, sondern vom
              Klassifikator beim Eingang. Bitte pruefen, bevor er rausgeht.</>
            ) : undefined}
          />
        ) : (
          <KeineAntwortVorgesehen
            reasoning={plan?.reasoning}
            hatEntwurf={!!draft.trim()}
            onTrotzdem={antwortTrotzdem}
          />
        )}
      </div>

      {/* Chat-Bar — bei Agent-Szenario triggert sie Re-Plan, sonst Draft-Regenerate */}
      <ClaudeChatBar
        ref={chatRef}
        onRegenerate={(p) => isAgentScenario ? replan.mutate(p) : regenerate.mutate(p)}
        pending={isAgentScenario ? replan.isPending : regenerate.isPending}
      />

      {/* „Besser planen" ist weg (Paul 28.10.2026): der Planer läuft schon
          auf GPT-6 Sol, der Knopf gab nur mehr Denkzeit. */}

      {/* Werkzeugzeile: Umformulieren, Terminanzahl und Anhaenge standen frueher
          in drei eigenen Zeilen untereinander. Auf einem Laptop blieben dem
          Entwurf dadurch 294 px, waehrend die Bedienelemente darunter 209 px
          belegten — praktisch halbe-halbe. Jetzt eine Zeile, die umbricht. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <QuickActionChips
        onTrigger={(p) => isAgentScenario ? replan.mutate(p) : regenerate.mutate(p)}
        pending={isAgentScenario ? replan.isPending : regenerate.isPending}
      />

      {/* Termin-Szenario: Vorschlag-Anzahl-Chips. Triggern Re-Plan damit der LLM
          den Reply-Body passend zur gewaehlten Anzahl neu formuliert. */}
      {plan && (plan.scenario === "neukunde_termin" || plan.scenario === "bestandskunde_termin") && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {[1, 2, 3].map((n) => {
            const slotsCurrent = [
              !!plan?.slot?.start_datetime,
              !!plan?.slot_secondary?.start_datetime,
              !!plan?.slot_tertiary?.start_datetime,
            ].filter(Boolean).length
            const isCurrent = slotsCurrent === n
            const hint = n === 1
              ? "Schlage NUR EINEN Termin vor (slot_secondary und slot_tertiary leer)."
              : n === 2
              ? "Schlage genau ZWEI Termine an verschiedenen Tagen vor (slot + slot_secondary; slot_tertiary leer)."
              : "Schlage DREI Termine an drei verschiedenen Tagen vor."
            return (
              <button
                key={n}
                type="button"
                onClick={() => replan.mutate(hint)}
                disabled={replan.isPending}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: isCurrent ? "#3C3489" : "#fff",
                  color: isCurrent ? "#fff" : "#3C3489",
                  border: `0.5px solid ${isCurrent ? "#3C3489" : "rgba(60,52,137,0.4)"}`,
                  borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 500,
                  cursor: replan.isPending ? "not-allowed" : "pointer",
                  opacity: replan.isPending ? 0.5 : 1,
                }}
                title={`Plan + Reply-Mail mit ${n} Termin${n > 1 ? "en" : ""} regenerieren`}
              >
                {isCurrent ? "✓ " : ""}{n} Vorschlag{n > 1 ? "e" : ""}
              </button>
            )
          })}
        </div>
      )}

      {/* Anhaenge zum Reply hinzufuegen — Teil derselben Werkzeugzeile */}
        <label style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "transparent", border: "0.5px solid rgba(44,44,42,0.2)",
          padding: "5px 10px", borderRadius: 6, cursor: "pointer", color: "#3C3489",
        }}>
          📎 Anhang hinzufuegen
          <input
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              const files = Array.from(e.target.files || [])
              if (files.length) setAttachments((prev) => [...prev, ...files])
              e.target.value = ""
            }}
          />
        </label>
        {attachments.map((f, i) => (
          <span key={i} style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: "#F5F4F0", padding: "3px 8px", borderRadius: 4,
            fontSize: 12, color: "#2C2C2A",
          }}>
            {f.name} ({Math.round(f.size / 1024)} KB)
            <button
              type="button"
              onClick={() => setAttachments(attachments.filter((_, idx) => idx !== i))}
              style={{ background: "none", border: "none", color: "#D85A30", cursor: "pointer", padding: "0 2px" }}
              title="Entfernen"
            >×</button>
          </span>
        ))}
        {/* Weicht die Verlauf-Einstellung von der Vorgabe ab, muss man das
            sehen — die Einstellung selbst liegt im Mehr-Menue. */}
        {!includeQuote && (
          <span
            title="Verlauf im Mehr-Menue wieder einschalten"
            style={{
              fontSize: 12, color: "#9A6B0E", background: "#FDF6E3",
              border: "0.5px solid rgba(154,107,14,0.3)", borderRadius: 999,
              padding: "3px 9px",
            }}
          >
            ohne Verlauf
          </span>
        )}
      </div>

      {/* Bestaetigungs-Buttons */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {isAgentScenario && nichtsGewaehlt ? (
          // Nichts angehakt, keine Antwort: dann ist Erledigen der naechste
          // Schritt — nicht ein gesperrter Ausfuehren-Knopf.
          <button
            type="button"
            onClick={() => discard.mutate()}
            disabled={discard.isPending}
            title="Nichts ausgewaehlt. Mail bleibt im Postfach, der Entwurf wird verworfen — Mail verschwindet aus dem Review."
            style={{
              background: "#3C3489", color: "#fff", border: "none",
              padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500,
              cursor: discard.isPending ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            {discard.isPending ? "Erledige…" : "✓ Ohne Antwort erledigt"}
          </button>
        ) : isAgentScenario ? (
          <button
            type="button"
            onClick={() => { void starteAusfuehrung(undefined) }}
            disabled={executePlan.isPending || vorschauLaeuft || (antwortAktiv && !draft.trim())}
            title={`Fuehrt ${knopfText.replace("✓ Ausfuehren: ", "")} aus. Klicke einzelne Boxen links zum Abwaehlen.`}
            style={{
              background: "#3C3489", color: "#fff", border: "none",
              padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500,
              cursor: executePlan.isPending ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            {executePlan.isPending ? "Fuehre aus…" : vorschauLaeuft ? "Einen Moment…" : knopfText}
            <kbd style={{
              fontSize: 12, background: "rgba(0,0,0,0.25)", padding: "1px 6px",
              borderRadius: 3, fontFamily: "ui-monospace, monospace",
            }}>⏎</kbd>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => { void starteSenden() }}
            disabled={send.isPending || !draft.trim() || !recipient.trim()}
            style={{
              background: "#1D9E75", color: "#fff", border: "none",
              padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500,
              cursor: send.isPending ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            {send.isPending ? "Sende…" : "✓ Senden"}
            <kbd style={{
              fontSize: 12, background: "rgba(0,0,0,0.25)", padding: "1px 6px",
              borderRadius: 3, fontFamily: "ui-monospace, monospace",
            }}>⏎</kbd>
          </button>
        )}
        {detail.forward_suggestion && (
          <button
            type="button"
            onClick={() => starteWeiterleiten(detail.forward_suggestion!.to_email)}
            disabled={forwardEmail.isPending}
            title={`Forward an ${detail.forward_suggestion.to_email} — gematcht auf "${detail.forward_suggestion.matched_term}"`}
            style={{
              background: "transparent", border: "0.5px solid #3C3489",
              color: "#3C3489", padding: "8px 14px", borderRadius: 8, fontSize: 13,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            → {forwardEmail.isPending ? "Leite weiter…" : detail.forward_suggestion.label}
          </button>
        )}
        <button
          type="button"
          onClick={() => setTaskModalOpen(true)}
          className="pp-btn pp-btn-sm"
          style={{
            background: "transparent", border: "0.5px solid #3C3489",
            color: "#3C3489", display: "inline-flex", alignItems: "center", gap: 6,
          }}
          title="Aufgabe in HERO anlegen — vorbefuellt mit Mail-Inhalt"
        >
          📋 Aufgabe erstellen
        </button>
        {/* Was seltener gebraucht wird, liegt hinter „Mehr" — sonst brauchte
            diese Leiste auf einem Laptop zwei Zeilen (80 px), die dem Entwurf
            fehlten. Senden und Aufgabe bleiben sichtbar. */}
        {/* Ist die Karte breit genug, stehen diese Aktionen direkt in der
            Leiste — dann gibt es nichts zu verstecken. Erst wenn der Platz
            knapp wird, klappen sie hinter „⋯ Mehr". Das entscheidet eine
            Container-Query in admin-theme.css, nicht dieser Code. */}
        <div className="review-mehr">
          <button
            type="button"
            onClick={() => setMehrOffen((o) => !o)}
            className="pp-btn pp-btn-sm review-mehr-knopf"
            aria-expanded={mehrOffen}
            aria-haspopup="menu"
            title="Weitere Aktionen"
          >
            ⋯ Mehr
          </button>
          {mehrOffen && (
            <button
              type="button"
              aria-label="Menue schliessen"
              onClick={() => setMehrOffen(false)}
              className="review-mehr-hinterlegung"
            />
          )}
          <div className={mehrOffen ? "review-mehr-liste offen" : "review-mehr-liste"}>
            <button
              type="button"
              onClick={() => { setMehrOffen(false); saveOnly.mutate() }}
              disabled={saveOnly.isPending}
              className="review-mehr-eintrag"
            >
              Nur speichern
            </button>
            <button
              type="button"
              onClick={() => { setMehrOffen(false); discard.mutate() }}
              disabled={discard.isPending}
              title="Mail bleibt im Postfach, der KI-Entwurf wird verworfen — Mail verschwindet aus dem Review-Tab. Nutze das wenn keine Antwort noetig ist."
              className="review-mehr-eintrag"
            >
              ✓ Ohne Antwort erledigt
            </button>
            <Link
              to={`/emails/${detail.email_id}`}
              onClick={() => setMehrOffen(false)}
              className="review-mehr-eintrag"
              title="Mail im E-Mail-Reiter oeffnen"
            >
              ↗ In E-Mails oeffnen
            </Link>
            <div className="review-mehr-trenner" />
            <label className="review-mehr-eintrag review-mehr-schalter">
              <input
                type="checkbox"
                checked={includeQuote}
                onChange={(e) => setIncludeQuote(e.target.checked)}
              />
              <span>Verlauf mitsenden</span>
            </label>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }} />
        <button
          type="button"
          onClick={() => onNext?.()}
          className="pp-btn pp-btn-sm"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          Naechster ↓
          <kbd style={{
            fontSize: 12, background: "rgba(44,44,42,0.1)", padding: "1px 6px",
            borderRadius: 3, fontFamily: "ui-monospace, monospace",
          }}>J</kbd>
        </button>
      </div>
      <CreateTaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        detail={detail}
        planTask={plan?.monteur_task}
      />
      <ActionConfirmDialog
        open={dialog !== null}
        lines={dialog?.lines ?? []}
        needsDouble={dialog?.needsDouble ?? true}
        busy={executePlan.isPending || send.isPending || forwardEmail.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={() => { const weiter = dialog?.weiter; setDialog(null); weiter?.() }}
      />
    </div>
  )
}

const ALLE_SCHRITTE = { contact: true, project: true, slot: true, monteur_task: true, reply: true }

/**
 * Was der Bestaetigen-Knopf tun wird.
 *
 * `mail` sagt, ob eine Antwort rausgeht — das entscheidet nicht der Plan
 * allein (er kann keine vorsehen, der Nutzer aber eine erzwingen), deshalb
 * kommt es von aussen. `weitere` sind die Katalog-Aktionen ohne Alt-Schritt
 * (Dokument ablegen, Angebot, Wiedervorlage) — ohne sie stand bei einem
 * Info-Plan „Nichts ausgewaehlt", obwohl zwei Aktionen angehakt waren.
 */
export function agentButtonLabel(plan: ActionPlan | undefined, mail: boolean, weitere: string[] = []): string {
  if (!plan) return "✓ Alles bestaetigen"
  const se = plan.step_enabled || ALLE_SCHRITTE
  const isTermin = plan.scenario === "neukunde_termin" || plan.scenario === "bestandskunde_termin"
  const parts: string[] = []
  if (isTermin) {
    if (se.contact && !plan.contact?.id) parts.push("Kontakt")
    if (se.project && !plan.project?.id) parts.push("Projekt")
    if (se.slot && plan.slot?.start_datetime) parts.push("Termin")
  }
  if (se.monteur_task && plan.monteur_task?.title) parts.push("Aufgabe")
  parts.push(...weitere)
  if (mail) parts.push("Mail")
  if (parts.length === 0) return "✓ Nichts ausgewaehlt"
  return `✓ Ausfuehren: ${parts.join(" + ")}`
}

/** Rechte Spalte, wenn der Plan keine Antwort vorsieht. Statt des
 *  Klassifikator-Entwurfs, der wie ein fertiger Brief aussah. */
function KeineAntwortVorgesehen({ reasoning, hatEntwurf, onTrotzdem }: {
  reasoning?: string
  hatEntwurf: boolean
  onTrotzdem: () => void
}) {
  return (
    <div style={{
      background: "#F5F4F0", border: "0.5px solid rgba(44,44,42,0.15)", borderRadius: 8,
      padding: 20, display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ fontSize: 14, fontWeight: 500, color: "#2C2C2A" }}>
        Der Plan sieht keine Antwort vor
      </div>
      {reasoning && (
        <div style={{ fontSize: 12, color: "#5F5E5A", fontStyle: "italic" }}>„{reasoning}"</div>
      )}
      <div style={{ fontSize: 12, color: "#5F5E5A", lineHeight: 1.5 }}>
        Links steht, was stattdessen passiert. Wer dem Kunden trotzdem schreiben
        will, oeffnet hier den Entwurf{hatEntwurf ? " — vorbelegt mit dem Vorschlag vom Eingang" : ""}.
      </div>
      <div>
        <button type="button" onClick={onTrotzdem} className="pp-btn pp-btn-sm"
          style={{ border: "0.5px solid #3C3489", color: "#3C3489", background: "transparent" }}>
          Trotzdem antworten
        </button>
      </div>
    </div>
  )
}

/** „Mail 1 von 3 im Verlauf · neuere Mail vom 02.09. (eigene Antwort)". */
function ThreadStandZeile({ detail }: { detail: DraftDetail }) {
  const st = detail.thread_stand
  if (!st || st.anzahl <= 1) return null
  const istNeueste = !st.neueste_id || st.neueste_id === detail.email_id
  const datum = (() => {
    try {
      return st.neueste_received_at
        ? new Date(st.neueste_received_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
        : ""
    } catch { return "" }
  })()
  return (
    <div style={{ fontSize: 12, color: "#888780", marginTop: 2 }}>
      Mail {st.position} von {st.anzahl} im Verlauf
      {!istNeueste && (
        <>
          {" · "}der Posteingang zeigt die neuere Mail{datum ? ` vom ${datum}` : ""}
          {st.neueste_richtung === "sent" ? " (eigene Antwort)" : ""}
          {" — "}
          <Link to={`/emails/${st.neueste_id}`} style={{ color: "#3C3489" }}>ansehen</Link>
        </>
      )}
    </div>
  )
}

function timeAgo(d: Date): string {
  const s = Math.floor((Date.now() - d.getTime()) / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m} Min`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} Std`
  const days = Math.floor(h / 24)
  return `${days}d`
}

// ── Newsletter-Variante: nur Original-Mail + Archivieren ───────────────
function NewsletterCard({
  detail, onArchive, archiving, onNext, onForward, forwarding,
}: {
  detail: DraftDetail; onArchive: () => void; archiving: boolean; onNext?: () => void
  onForward?: (to: string) => void; forwarding?: boolean
}) {
  const received = detail.received_at ? new Date(detail.received_at) : null
  const ago = received ? timeAgo(received) : ""
  return (
    <div style={{
      background: "#fff", borderRadius: 12, border: "0.5px solid rgba(44,44,42,0.15)",
      padding: 24, display: "flex", flexDirection: "column", gap: 16, height: "100%",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{
          background: "#F1F5F9", color: "#475569", border: "0.5px solid #CBD5E1",
          padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500,
        }}>Newsletter</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 500, color: "#2C2C2A" }} data-pii>{detail.sender || detail.sender_email}</div>
          <div style={{ fontSize: 12, color: "#888780", marginTop: 2 }}>
            <span data-pii>{detail.subject || "(ohne Betreff)"}</span>
            {ago && <> · vor {ago}</>}
            {" · "}<span>{detail.tenant_name}</span>
          </div>
        </div>
      </div>
      <div style={{
        background: "#fff", borderRadius: 8, padding: 0, flex: 1,
        overflowY: "auto", border: "0.5px solid rgba(44,44,42,0.08)",
      }}>
        {detail.original_html ? (
          <EmailBody html={detail.original_html} minHeight={300} />
        ) : (
          <div data-pii style={{
            padding: 18, whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.6, color: "#2C2C2A",
          }}>
            {detail.original_body || detail.original_excerpt || "(kein lesbarer Inhalt — vermutlich Bild-Mail)"}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          onClick={onArchive}
          disabled={archiving}
          style={{
            background: "#3C3489", color: "#fff", border: "none",
            padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500,
            cursor: archiving ? "not-allowed" : "pointer",
          }}
        >
          {archiving ? "Archiviere…" : "✓ Archivieren"}
        </button>
        {detail.forward_suggestion && onForward && (
          <button
            type="button"
            onClick={() => onForward(detail.forward_suggestion!.to_email)}
            disabled={!!forwarding}
            title={`Forward an ${detail.forward_suggestion.to_email} — gematcht auf "${detail.forward_suggestion.matched_term}"`}
            style={{
              background: "transparent", border: "0.5px solid #3C3489",
              color: "#3C3489", padding: "8px 14px", borderRadius: 8, fontSize: 13,
              cursor: forwarding ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            → {forwarding ? "Leite weiter…" : detail.forward_suggestion.label}
          </button>
        )}
        <Link
          to={`/emails/${detail.email_id}`}
          className="pp-btn pp-btn-sm"
          style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          ↗ In E-Mails oeffnen
        </Link>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => onNext?.()} className="pp-btn pp-btn-sm">
          Naechster ↓
        </button>
      </div>
    </div>
  )
}

